import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { PartsList } from "./PartsEditor";
import { Attachments } from "./Attachments";

const printVoucher = () => {
  const el = document.getElementById("voucher-print");
  const w = window.open("", "_blank", "width=820,height=1000");
  if (!w) return window.print();
  const styles = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((n) => n.outerHTML).join("");
  w.document.write(`<!doctype html><html lang="${document.documentElement.lang}" dir="${el.getAttribute("dir")}"><head><meta charset="utf-8"><title>${el.querySelector("[data-testid=voucher-number]")?.textContent || ""}</title>${styles}<style>@page{size:A4;margin:14mm}body{background:#fff;padding:0;margin:0}#voucher-print{position:static!important;max-width:720px;margin:0 auto}</style></head><body>${el.outerHTML}</body></html>`);
  w.document.close();
  w.onload = () => { w.focus(); w.print(); };
  setTimeout(() => { if (!w.closed) { w.focus(); w.print(); } }, 1200);
};

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-dashed border-slate-300">
    <span className="text-slate-500 text-sm">{label}</span>
    <span className={mono ? "plate text-sm" : "font-bold text-sm"}>{value || "-"}</span>
  </div>
);

export const VoucherDialog = ({ data, onOpenChange }) => {
  const { t, fmt, lang } = useI18n();
  const [ws, setWs] = useState(null);
  useEffect(() => { if (data) api.get("/settings").then((r) => setWs(r.data)); }, [data]);
  if (!data) return null;
  const { car, voucher } = data;
  return (
    <Dialog open={!!data} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="voucher-dialog">
        <div id="voucher-print" dir={lang === "ar" ? "rtl" : "ltr"} className="space-y-4">
          <div className="flex items-start justify-between border-b-2 border-[#3E3E40] pb-3">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="" className="w-10 h-10 object-contain" />
              <div><div className="font-extrabold">{t("appName")}</div></div>
            </div>
            <div className="text-end">
              <div className="text-xs text-slate-500">{t("voucherNo")}</div>
              <div className="font-mono font-black" data-testid="voucher-number">{voucher.number}</div>
            </div>
          </div>
          <DialogTitle asChild><h3 className="text-center text-xl font-extrabold" data-testid="voucher-title">{voucher.kind === "return" ? (voucher.includes_car ? t("returnVoucher") : t("partsReturnVoucher")) : voucher.includes_car ? t("voucher") : t("partsVoucher")}</h3></DialogTitle>
          <DialogDescription className="sr-only">{voucher.number}</DialogDescription>
          <div>
            <Row label={t("plate")} value={car.plate_number} mono />
            <Row label={t("carType")} value={car.car_type} />
            <Row label={t("supplier")} value={car.supplier_name} />
            <Row label={t("shelf")} value={car.shelf_number} />
            <Row label={t("entryDate")} value={fmt(car.history[0]?.date)} />
            <Row label={voucher.kind === "return" ? t("returnDate") : t("dispatchDate")} value={fmt(voucher.date)} />
            <Row label={t("voucherType")} value={voucher.kind === "return" ? (voucher.includes_car ? t("returnModeAll") : t("returnPartsOnly")) : voucher.includes_car ? t("modeAll") : t("partsOnly")} />
            {voucher.reason && <Row label={t("returnReason")} value={voucher.reason} />}
            <Row label={t("employeeName")} value={voucher.by_name} />
            <Row label={voucher.kind === "return" ? t("supplierRep") : t("recipientName")} value={voucher.recipient_name} />
            {voucher.note && <Row label={t("note")} value={voucher.note} />}
          </div>
          <PartsList parts={voucher.parts} testId="voucher-parts" />
          <div className="grid grid-cols-2 gap-6 pt-8 text-sm">
            <div><div className="text-slate-500">{t("employeeName")} / {t("signature")}</div><div className="border-b border-slate-400 h-10" /></div>
            <div><div className="text-slate-500">{voucher.kind === "return" ? t("supplierRep") : t("recipientName")} / {t("signature")}</div><div className="border-b border-slate-400 h-10" /></div>
          </div>
          {ws && (ws.workshop_phone || ws.workshop_address || ws.workshop_cr) && (
            <div className="mt-6 pt-3 border-t-2 border-[#F5BE25] flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-slate-600" data-testid="voucher-workshop-info">
              {ws.workshop_phone && <span>{t("workshopPhone")}: <b dir="ltr">{ws.workshop_phone}</b></span>}
              {ws.workshop_address && <span>{t("workshopAddress")}: <b>{ws.workshop_address}</b></span>}
              {ws.workshop_cr && <span>{t("workshopCr")}: <b dir="ltr">{ws.workshop_cr}</b></span>}
            </div>
          )}
        </div>
        <Attachments carId={car.id} voucherNumber="invoices" title={t("invoices")} testId="voucher-invoices-section" />
        <Attachments carId={car.id} voucherNumber={voucher.number} />
        <div className="no-print flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("close")}</Button>
          <Button data-testid="voucher-print-btn" onClick={printVoucher}><Printer className="w-4 h-4" />{t("print")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
